import { AfterViewInit, Component, OnDestroy, ViewChild } from "@angular/core";
import { get } from "lodash";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";
import { ShiftSignOnComponent } from "ng/src/app/shifts/sign-on/shift-sign-on.component";
import { BehaviorSubject, merge, of, Subject, Subscription, timer } from "rxjs";
import { catchError, exhaustMap, finalize, switchMap, takeUntil } from "rxjs/operators";
import { Utils } from "ng/src/app/core/utils";
import { LogService } from "ng/src/app/logs/log.service";
import { LogRoleId } from "ng/src/app/logs/log.model";
import { ConfirmationService } from "primeng/api";
import { DatePipe } from "@angular/common";
import { ShiftDisplayModel } from "ng/src/app/shifts/shift.model";
import { Dialog } from "primeng/dialog";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { OverlayPanel } from "primeng/overlaypanel";

@Component({
	selector: 'shift-indicator',
	templateUrl: './shift-indicator.component.html',
	styleUrls: ['./shift-indicator.component.scss'],
	providers: [DatePipe]
})
export class ShiftIndicatorComponent implements AfterViewInit, OnDestroy {
	@ViewChild('ed') extensionDialog?: Dialog
	@ViewChild('overlayPanel') overlayPanel?: OverlayPanel
	@ViewChild('signOnComponent') signOnComponent?: ShiftSignOnComponent

	constructor(
		private shiftService: ShiftsService,
		private datePipe: DatePipe,
		private confirmService: ConfirmationService
	) {}
	
	private destroy$ = new Subject();
	private extensionMessage$ = new BehaviorSubject('');
	private shiftDate?: Date
	private popupSub?: Subscription
	private _dialogVisible = false;

	overlayPanelText = '';
	signOffPending = false;
	extensionPending = false;
	activeShifts: ShiftDisplayModel[] = [];
	
	get dialogVisible() {
		return this._dialogVisible;
	}
	
	set dialogVisible(val: boolean) {
		this._dialogVisible = val;
		if (!val) {
			this.signOnComponent?.reset();
		}
	}

	get userShift() {
		return this.shiftService.userShift;
	}
	
	get minutesLeft() {
		if (!this.shiftDate) return -1;
		const val = Math.ceil((this.shiftDate.getTime() - new Date().getTime()) / 60000);
		this.extensionMessage$.next(`Your current shift ends in ${val} minute${val === 1 ? '' : 's'} at ${this.datePipe.transform(this.shiftDate, 'HH:mm')}.`)
		return val;
	}
	
	get displayExtension() {
		return Utils.isInRange(this.minutesLeft, 1, 10);
	}
	
	get canWrite() {
		return LogService.selectedLog?.userRole ?? LogRoleId.READ !== LogRoleId.READ;
	}
	
	get hoverSupported() {
		return AppStateService.hoverSupported;
	}
	
	ngAfterViewInit(): void {
		timer(0, 60000)
			.pipe(
				takeUntil(this.destroy$),
				exhaustMap(() => merge(
					this.shiftService.refreshActiveShifts().pipe(
						catchError((err) => {
							console.error(err);
							return of();
						})
					)
				))
			)
			.subscribe();

		this.extensionMessage$.pipe(
			takeUntil(this.destroy$)
		)
			.subscribe(val => {
				// Sets message of extension confirm dialog through HTML because changing the
				// message afterwards using various methods does not seem to work.
				if (!this.extensionDialog || !this.extensionDialog.visible) return;
				const el = this.extensionDialog.contentViewChild?.nativeElement as HTMLElement;
				const messageElement = el?.getElementsByClassName('p-confirm-dialog-message')[0];
				if (!messageElement) return;
				messageElement.innerHTML = val;
			})
		
		this.shiftService.activeShifts$
			.pipe(
				takeUntil(this.destroy$)
			)
			.subscribe((shifts) => {
				if ((!this.shiftService.isOnShift || !this.displayExtension) && this.extensionDialog?.visible) {
					this.extensionDialog.visible = false;
				}

				this.activeShifts = shifts.map(s => {
					const start = new Date(s.beginTime).getDate();
					const end = new Date(s.endTime).getDate();
					const useDates = start !== end;
					const format = (useDates ? 'MM/dd ' : '') + 'HH:mm'
					return {
						...s,
						safeToolTip: `${this.datePipe.transform(s.beginTime, format)} - ${this.datePipe.transform(s.endTime, format)} ${s.role ? (' as ' + s.role.name) : ''}`,
						toolTip: `${this.datePipe.transform(s.beginTime, format)} -<br> ${this.datePipe.transform(s.endTime, format)} ${s.role ? ('<br>as ' + s.role.name) : ''}`
					}
				})
					.sort(Utils.createMultiSortFunction([
						[s => new Date(s.endTime), 'asc'],
						[s => s.user.lastName, 'asc'],
						[s => s.user.firstName, 'asc']
					]))
				
				if (!this.userShift || !this.userShift.millisRemaining) {
					delete this.shiftDate;
					return
				}
				this.shiftDate = new Date(new Date().getTime() + this.userShift.millisRemaining);
				
				this.popupSub?.unsubscribe();
				const alertDelay = this.userShift.millisRemaining - 540_000;
				if (alertDelay < 0 || !Utils.isSafeNumber(alertDelay)) return;
				
				this.popupSub = timer(alertDelay)
					.pipe(takeUntil(this.destroy$))
					.subscribe(() => this.showExtensionConfirm())
			})
	}
	
	signOnToShift() {
		this.dialogVisible = true;
	}
	
	extendShift() {
		if (!this.userShift || this.extensionPending) return;
		this.extensionPending = true;
		
		this.shiftService.extendShift(this.userShift)
			.pipe(
				switchMap(() => this.shiftService.refreshActiveShifts()),
				finalize(() => this.extensionPending = false)
			)
			.subscribe()
	}
	
	exitShift() {
		if (!this.userShift || this.signOffPending) return

		const shift = this.userShift;
		this.confirmService.confirm({
			key: 'close',
			rejectButtonStyleClass: 'p-button-secondary',
			header: 'Shift Sign Out',
			defaultFocus: 'reject',
			message: `You are currently on shift until ${this.datePipe.transform(shift.endTime, 'HH:mm')}. Sign out of shift early?`,
			accept: () => {
				this.signOffPending = true;
				this.shiftService.signOut(shift)
					.pipe(
						switchMap(() => this.shiftService.refreshActiveShifts()),
						finalize(() => this.signOffPending = false)
					)
					.subscribe(() => {
						delete this.shiftDate;
					})
			}
		})
	}
	
	showExtensionConfirm() {
		this.confirmService.confirm({
			key: 'shiftExtendDialog',
			header: 'Shift Ending Soon',
			rejectButtonStyleClass: 'p-button-secondary',
			defaultFocus: 'accept',
			rejectLabel: 'Extend Shift',
			rejectIcon: 'pi pi-clock',
			acceptLabel: 'Ok',
			reject: () => { this.extendShift() }
		})
	}
	
	showOverlayPanel(text: string, event: Event) {
		this.overlayPanelText = text;
		this.overlayPanel?.show(event);
	}
	
	timeoutSignOn() {
		setTimeout(() => {
			this.dialogVisible = false;
		}, 3_000);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
	}
}